import axios from 'axios'

export interface Wallet {
  id: string
  user_id: string
  user_name?: string
  user_email?: string
  balance: number
  currency: string
  status: 'active' | 'frozen' | 'closed'
  created_at: string
  updated_at: string
  last_transaction_at?: string
}

export interface WalletTransaction {
  id: string
  wallet_id: string
  user_id?: string
  user_name?: string
  type: 'top_up' | 'payment' | 'refund' | 'credit_adjustment' | 'debit_adjustment'
  amount: number
  currency: string
  description: string
  reference_type: string | null
  reference_id: string | null
  status: 'pending' | 'completed' | 'failed' | 'cancelled'
  created_at: string
}

export interface CreditAdjustmentDto {
  user_id: string
  amount: number
  reason: string
}

export interface FraudFlag {
  id: string
  wallet_id: string
  user_id: string
  user_name?: string
  flag_type: string
  severity: 'low' | 'medium' | 'high'
  description: string
  created_at: string
  resolved: boolean
}

const API_BASE_URL = '/api'

const walletApi = axios.create({
  baseURL: `${API_BASE_URL}/wallet`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
walletApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor for error handling
walletApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const walletService = {
  // === Wallets ===
  async getWallets(params?: {
    search?: string
    status?: string
    min_balance?: number
    max_balance?: number
    limit?: number
    offset?: number
  }): Promise<{ success: boolean; data: Wallet[]; total: number }> {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.append('search', params.search)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.min_balance !== undefined) searchParams.append('min_balance', params.min_balance.toString())
    if (params?.max_balance !== undefined) searchParams.append('max_balance', params.max_balance.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())

    const response = await walletApi.get('/admin/wallets', { params: searchParams })
    return response.data
  },

  async getWalletById(id: string): Promise<{ success: boolean; data: Wallet }> {
    const response = await walletApi.get(`/admin/wallets/${id}`)
    return response.data
  },

  async freezeWallet(id: string): Promise<{ success: boolean; data: Wallet; message: string }> {
    const response = await walletApi.post(`/admin/wallets/${id}/freeze`)
    return response.data
  },

  async unfreezeWallet(id: string): Promise<{ success: boolean; data: Wallet; message: string }> {
    const response = await walletApi.post(`/admin/wallets/${id}/unfreeze`)
    return response.data
  },

  // === Credit Adjustment ===
  async creditAdjustment(data: CreditAdjustmentDto): Promise<{ success: boolean; data: WalletTransaction; message: string }> {
    const response = await walletApi.post('/admin/credit-adjustment', data)
    return response.data
  },

  // === Transactions ===
  async getTransactions(params?: {
    wallet_id?: string
    user_id?: string
    type?: string
    status?: string
    from_date?: string
    to_date?: string
    limit?: number
    offset?: number
  }): Promise<{ success: boolean; data: WalletTransaction[]; total: number }> {
    const searchParams = new URLSearchParams()
    if (params?.wallet_id) searchParams.append('wallet_id', params.wallet_id)
    if (params?.user_id) searchParams.append('user_id', params.user_id)
    if (params?.type) searchParams.append('type', params.type)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.from_date) searchParams.append('from_date', params.from_date)
    if (params?.to_date) searchParams.append('to_date', params.to_date)
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())

    const response = await walletApi.get('/admin/transactions', { params: searchParams })
    return response.data
  },

  // === Fraud Detection ===
  async getFraudFlags(params?: {
    resolved?: boolean
    severity?: string
    limit?: number
    offset?: number
  }): Promise<{ success: boolean; data: FraudFlag[]; total: number }> {
    const searchParams = new URLSearchParams()
    if (params?.resolved !== undefined) searchParams.append('resolved', params.resolved.toString())
    if (params?.severity) searchParams.append('severity', params.severity)
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.offset) searchParams.append('offset', params.offset.toString())

    const response = await walletApi.get('/admin/fraud-flags', { params: searchParams })
    return response.data
  },

  async resolveFraudFlag(id: string): Promise<{ success: boolean; message: string }> {
    const response = await walletApi.post(`/admin/fraud-flags/${id}/resolve`)
    return response.data
  },
}

export default walletService
